import { FormArray } from '@angular/forms';
import { IUser, IUserClientReference } from '@app/auth/models/user';
import { CartItem, CartItemFormItem } from '@pages/cart/models/cart.model';
import { NoteDeliverySelector, NOTE_DELIVERY_CONDITIONS } from '@app/pages/users/models/users.model';
import { CartItemUpdate, CartItemUpdateRequest } from '@app/shared/models/product.model';

export interface ClientReferencesState {
  userClientRefValues: IUserClientReference[];
  userClientRefObject: { [id: number]: IUserClientReference };
  showUserClientRef: boolean;
}

export interface NoteDeliverySelectorsState {
  noteDeliveryValues: NoteDeliverySelector[];
  noteDeliveryValuesCentral: NoteDeliverySelector[];
}

export interface ProductCleanState {
  are_clean_fields: boolean;
  are_clean_field_note: boolean;
}

export function trackByCartItem(_index: number, item: CartItem): string {
  return `${item.card}-${item.is_central}-${item.warehouse}-${item.note}-${item.ndo}`;
}

export function buildClientReferencesState(user: IUser | null): ClientReferencesState {
  if (user && user.profile.selected_client_references.length > 0) {
    const userClientRefValues = [{ client: user.profile.client }, ...user.profile.selected_client_references];
    const userClientRefObject = userClientRefValues.reduce((accumulator, value) => {
      accumulator[value.client.id] = value;
      return accumulator;
    }, {} as { [id: number]: IUserClientReference });

    return {
      userClientRefValues,
      userClientRefObject,
      showUserClientRef: true,
    };
  }

  return {
    userClientRefValues: [],
    userClientRefObject: {},
    showUserClientRef: false,
  };
}

export function buildNoteDeliverySelectors(
  translate: (key: string) => string,
  loggedInUser: IUser | null,
): NoteDeliverySelectorsState {
  let noteDeliveryValues = NOTE_DELIVERY_CONDITIONS
    .filter(ndo => !ndo.showOnlyOnCentral)
    .map(ndo => ({
      label: translate(ndo.label),
      value: ndo.formName,
      isDefault: ndo.isDefault,
    }));

  let noteDeliveryValuesCentral = NOTE_DELIVERY_CONDITIONS
    .map(ndo => ({
      label: translate(ndo.label),
      value: ndo.formName,
      isDefault: ndo.isDefault,
    }));

  if (
    loggedInUser
    && loggedInUser.profile.user_field_values
    && (loggedInUser.profile.note_choice_on_add_cart || loggedInUser.profile.company.note_choice_on_add_cart)
  ) {
    const allowedKeys = loggedInUser.profile.user_field_values.map(ufv => ufv.key);
    noteDeliveryValues = noteDeliveryValues.filter(ndo => allowedKeys.includes(ndo.value));
    noteDeliveryValuesCentral = noteDeliveryValuesCentral.filter(ndo => allowedKeys.includes(ndo.value));
  }

  return {
    noteDeliveryValues,
    noteDeliveryValuesCentral,
  };
}

export function getUserClientSelectorLabel(
  id: number,
  userClientRefObject: { [key: number]: IUserClientReference },
): string | number {
  const clientRef = userClientRefObject[id];
  if (clientRef) {
    return `${clientRef.client.skuba_client_id} - ${clientRef.client.name}`;
  }
  return id;
}

export function clearFormArray(formArray: FormArray): void {
  while (formArray.controls.length > 0) {
    formArray.removeAt(formArray.controls.length - 1);
  }
}

export function getProductCleanState(product: CartItem, formValue: CartItemFormItem): ProductCleanState {
  let are_clean_fields = true;
  let are_clean_field_note = true;

  if (
    formValue.quantity !== product.quantity
    || formValue.ndo !== product.ndo
    || formValue.client_ref_id !== product.client_ref_id
  ) {
    are_clean_fields = false;
  }

  if (formValue.note !== product.note) {
    are_clean_fields = false;
    are_clean_field_note = false;
  }

  return {
    are_clean_fields,
    are_clean_field_note,
  };
}

export function hasUnsavedCartProducts(products: CartItem[]): boolean {
  return products.some(product => product && !product.are_clean_fields);
}

export function buildCartItemUpdate(
  product: CartItem,
  formItem: CartItemFormItem,
  showFifoStocks: boolean,
): CartItemUpdate {
  const data: CartItemUpdate = {
    id: +product.id,
    product_id: +product.product_id,
    card_number: +formItem.card,
    warehouse_code: formItem.warehouse,
    quantity: +formItem.quantity,
    note: formItem.note,
    ndo: formItem.ndo,
    client_ref_id: formItem.client_ref_id,
    is_central: formItem.is_central,
    is_carried: formItem.is_carried,
    on_the_way: formItem.on_the_way,
  };

  if (showFifoStocks) {
    data.skn = product.skn;
    data.deposit = product.deposit;
    data.brand = product.brand;
    data.quantity -= product.quantity;
    data.oldQuantity = product.quantity;
  }

  return data;
}

export function collectUnsavedProductUpdates(
  products: CartItem[],
  formProducts: CartItemFormItem[],
  showFifoStocks: boolean,
  matchId: string,
): CartItemUpdateRequest[] {
  const productsToUpdate: CartItemUpdateRequest[] = [];

  for (let i = 0; i < products.length; i++) {
    const product = products[i];
    if (!product || product.are_clean_fields) {
      continue;
    }

    const cartItemFormItem = formProducts[i];
    productsToUpdate.push({
      update: buildCartItemUpdate(product, cartItemFormItem, showFifoStocks),
      skn: product.skn,
      product,
      matchId,
    });
  }

  return productsToUpdate;
}

export function normalizeCartQuantity(
  rawQuantity: number,
  increment: number,
): number {
  let inputNum = +rawQuantity;

  if (Number.isNaN(inputNum)) {
    return increment;
  }

  if (inputNum < 0) {
    inputNum = 0;
  }

  if (inputNum % increment !== 0 || inputNum === 0) {
    let roundedQuantity = inputNum + increment - (inputNum % increment);
    if (roundedQuantity < increment) {
      roundedQuantity = increment;
    }
    return roundedQuantity;
  }

  return inputNum;
}
